import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, Truck, Headphones, Cake, Camera, Scale, Images, Stamp } from "lucide-react";
import { buildFreshnessStampData } from "@/lib/printFreshnessStamp";
import { compressImageForUpload, uploadFormWithRetry } from "@/lib/driverImageUpload";
import { appPathname } from "@/lib/opsBase";
import {
  productionAuthBody,
  productionFormAuth,
  productionTaskQuery,
} from "@/lib/productionHubAuth";
import FrosterStructureTable from "@/components/FrosterStructureTable";
import ProductionCustomerCall from "@/components/ProductionCustomerCall";
import { OrderBakingSpecDisplay } from "@/components/OrderBakingSpecDisplay";
import { formatKitchenTierLabel } from "@/lib/kitchenTierLabel";
import PriorityOrderBanner from "@/components/PriorityOrderBanner";
import { isPriorityOrderActive } from "@/lib/priorityOrder";
import { isPriorityTodayOrderActive } from "@/lib/dailyOrder";
import { displayCustomerName } from "@/lib/customerName";
import { whatsappHref } from "@/features/customers/lib/phoneUtils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PRODUCTION_DRIVER_PAGE_CSS } from "@/lib/productionDriverPageStyles";
import KitchenTaskScheduleBar, { KITCHEN_TASK_SCHEDULE_BAR_CSS } from "@/components/mobile/KitchenTaskScheduleBar";
import ProductionStageStartBar from "@/components/mobile/ProductionStageStartBar";
import KitchenUnitLabel from "@/components/mobile/KitchenUnitLabel";
import {
  isProductionStageStarted,
  isRtgDecorSkip,
  mergeProductionTaskInfo,
  postProductionStageStart,
} from "@/lib/productionStageTiming";

function isPastryTask(info) {
  return Boolean(info?.is_pastry || info?.is_pastry_decor_only || info?.ka_pastry_decor_only);
}

function taskReferenceImage(info) {
  return info?.pastry_image_url || info?.cake_image_url || "";
}

function taskWeightOrPiecesLabel(info) {
  if (isPastryTask(info)) {
    const pcs = info?.pastry_pieces || info?.quantity;
    return pcs ? `${pcs} pcs` : "Pastry";
  }
  return info?.weight_kg ? `${info.weight_kg} kg` : null;
}

function readTokenFromQuery() {
  const q = new URLSearchParams(window.location.search);
  return (q.get("token") || "").trim();
}

/**
 * Priority styling for KA task screens. Uses the same `today_order_kind` signal
 * as the dashboard list so the task view and the list agree. Falls back to the
 * legacy is_priority flag only when the field is absent, so nothing regresses.
 */
function taskPriorityActive(order) {
  if (order && order.today_order_kind != null) {
    return isPriorityTodayOrderActive(order);
  }
  return isPriorityOrderActive(order);
}

/** Resolve JWT from ?token= or from /p/{role}/{shortCode} via API (WhatsApp short links). */
async function resolveProductionToken(expectedRole) {
  const fromQuery = readTokenFromQuery();
  if (fromQuery) return fromQuery;

  const pathMatch = appPathname().match(
    /^\/p\/(baker|froster|decorator)\/([A-Za-z0-9]+)\/?$/i
  );
  if (!pathMatch) return "";

  const pathRole = pathMatch[1].toLowerCase();
  const code = pathMatch[2];
  if (pathRole !== (expectedRole || "").toLowerCase()) return "";

  const res = await fetch(`/api/p/${encodeURIComponent(pathRole)}/${encodeURIComponent(code)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.redirect_url) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : "Invalid or expired link. Ask manager to resend WhatsApp."
    );
  }
  const u = new URL(data.redirect_url, window.location.origin);
  return (u.searchParams.get("token") || "").trim();
}

function readLinkErrorFromQuery() {
  const q = new URLSearchParams(window.location.search);
  const err = (q.get("error") || "").trim();
  if (err === "invalid") {
    return "This kitchen link is invalid or expired. Ask manager for a fresh link.";
  }
  if (err === "fetch") {
    return "Could not verify this link. Check connection and try the WhatsApp link again.";
  }
  return "";
}

/** Commas in orders-app cheesecake filenames must be encoded for reliable loading. */
function cakeImageSrc(url) {
  if (!url || typeof url !== "string") return url;
  try {
    const u = new URL(url, window.location.origin);
    return u.href;
  } catch {
    return encodeURI(url);
  }
}

function parseProductionError(detail, fallback = "Request failed") {
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || d?.message || String(d)).filter(Boolean).join("; ") || fallback;
  }
  return fallback;
}

async function parseJson(res) {
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseProductionError(j?.detail));
  return j;
}

const roleTitle = {
  baker: "Baker",
  froster: "Froster",
  decorator: "Decorator",
};

import {
  hubProfileLoginGateMessage,
} from "@/lib/kitchenHubProfile";

function stageStartGateMessage(info, role) {
  const loginGate = hubProfileLoginGateMessage();
  if (loginGate) return loginGate;
  if (!info) return "";
  if (isRtgDecorSkip(info) && (role === "baker" || role === "froster")) {
    return info?.is_pastry || info?.is_pastry_decor_only
      ? "Pastry order — open Decorator tab (box from fridge, no baking or frosting)."
      : "RTG fridge stock — open Decorator tab for this order (no baking or frosting).";
  }
  if (role === "froster" && !info.production_baker_done_at) {
    return "Waiting for baker Done before you can Start.";
  }
  if (role === "decorator") {
    if (info.is_rtg_decor_only || info.rtg_decor_skip_baker_froster) {
      return "";
    }
    if (info.is_dry_cake || info.dry_cake_skip_froster) {
      if (!info.production_baker_done_at) return "Waiting for baker Done before you can Start.";
    } else if (!info.production_froster_done_at) {
      return "Waiting for froster Done before you can Start.";
    }
  }
  return "";
}

function CakeBlockLabel({ label }) {
  return (
    <div className="rounded-xl border-2 border-[#EE2677]/35 bg-[#FCE7F3] px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wide text-[#C2185B]">Cake</p>
      <p className="text-sm font-extrabold leading-tight text-[#2D2D2D]">{label}</p>
    </div>
  );
}

function FrosterPhotoCapture({
  previewSrc,
  hasSaved,
  uploading,
  disabled,
  inputKey,
  onFileSelected,
  variant = "driver",
}) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || disabled || uploading) return;
    await onFileSelected(file);
  };

  const cameraBtn =
    variant === "driver" ? (
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => cameraRef.current?.click()}
        className="driver-btn driver-btn-dark"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
      >
        <Camera size={20} aria-hidden />
        {uploading ? "…" : hasSaved || previewSrc ? "Retake" : "Camera"}
      </button>
    ) : (
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => cameraRef.current?.click()}
        className="flex items-center justify-center gap-2 rounded-xl bg-[#1a1a1a] px-4 py-3.5 text-sm font-bold text-white disabled:opacity-50"
      >
        <Camera size={18} aria-hidden />
        {uploading ? "…" : hasSaved || previewSrc ? "Retake" : "Camera"}
      </button>
    );

  const galleryBtn =
    variant === "driver" ? (
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => galleryRef.current?.click()}
        className="driver-btn driver-btn-teal"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
      >
        <Images size={20} aria-hidden />
        Gallery
      </button>
    ) : (
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => galleryRef.current?.click()}
        className="flex items-center justify-center gap-2 rounded-xl bg-teal-700 px-4 py-3.5 text-sm font-bold text-white disabled:opacity-50"
      >
        <Images size={18} aria-hidden />
        Gallery
      </button>
    );

  const inputs = (
    <div key={inputKey}>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        disabled={disabled || uploading}
        onChange={onChange}
        style={{ display: "none" }}
        aria-hidden
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        disabled={disabled || uploading}
        onChange={onChange}
        style={{ display: "none" }}
        aria-hidden
      />
    </div>
  );

  const previewBlock =
    hasSaved || previewSrc ? (
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <div
          className={
            variant === "driver"
              ? "driver-text-sm driver-text-green driver-font-medium driver-mb-3"
              : "mb-2 text-sm font-semibold text-green-700"
          }
        >
          {uploading ? "Uploading photo…" : hasSaved ? "✓ Frosted photo saved" : "Saving photo…"}
        </div>
        {previewSrc ? (
          <img
            src={previewSrc}
            alt="Frosted cake"
            className={variant === "driver" ? "driver-door-thumb" : "mx-auto max-h-56 w-full rounded-2xl border border-green-200 object-cover"}
            loading="lazy"
            decoding="async"
          />
        ) : null}
      </div>
    ) : null;

  if (variant === "driver") {
    return (
      <div className="driver-card" style={{ borderColor: "#15803D", background: "#F0FDF4" }}>
        <div className="driver-label">Frosted cake photo</div>
        <p className="driver-call-hint" style={{ marginBottom: 8 }}>
          Take a photo of the frosted cake. Customer gets a compare link when you tap Done.
        </p>
        {previewBlock}
        {inputs}
        <div className="driver-grid-2">
          {cameraBtn}
          {galleryBtn}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border-2 border-green-300 bg-green-50 p-3">
      <p className="mb-1 text-base font-bold text-[#166534]">Frosted cake photo</p>
      <p className="mb-3 text-sm text-[#166534]/80">Take a photo of the frosted cake before marking done.</p>
      {previewBlock}
      {inputs}
      <div className="grid grid-cols-2 gap-2.5">
        {cameraBtn}
        {galleryBtn}
      </div>
    </div>
  );
}

function IngredientList({ title, subtitle, items, accent }) {
  const divider = accent.divider || "border-[#EBB3A9]/40";
  if (!items?.length) {
    return (
      <div className={`overflow-hidden rounded-2xl border ${accent.border} ${accent.bg}`}>
        <div className={`border-b px-3 py-2 ${divider} ${accent.bg}`}>
          <p className={`text-xs font-extrabold uppercase tracking-wide ${accent.title}`}>{title}</p>
        </div>
        <p className="px-3 py-3 text-xs text-[#888]">None for this order</p>
      </div>
    );
  }
  return (
    <div className={`overflow-hidden rounded-2xl border ${accent.border} ${accent.bg}`}>
      <div className={`flex items-baseline justify-between gap-2 border-b px-3 py-2 ${divider}`}>
        <p className={`text-xs font-extrabold uppercase tracking-wide ${accent.title}`}>{title}</p>
        <p className={`text-[11px] font-semibold ${accent.subtitle}`}>{subtitle}</p>
      </div>
      <table className="w-full border-collapse text-sm">
        <tbody>
          {items.map((item, i) => (
            <tr key={`${item.name}-${i}`} className={`border-b ${divider} last:border-b-0`}>
              <td className={`border-r px-3 py-2 align-top font-semibold leading-tight text-[#2D2D2D] ${divider}`}>
                {item.name}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right align-top font-bold leading-tight text-[#2D2D2D]">
                {item.amount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BakerPrepIntel({ info }) {
  if (!info || info.role !== "baker") return null;
  const prep = info.prep_instructions;
  const hint = info.bake_time_hint;
  if (!prep && !(hint && hint.text)) return null;
  return (
    <div className="mt-4 space-y-2">
      {hint && hint.text ? (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 px-3 py-2">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-amber-800">
            Bake time
          </p>
          <p className="mt-0.5 text-sm font-bold text-amber-900">{hint.text}</p>
        </div>
      ) : null}
      {prep ? (
        <div className="rounded-2xl border border-sky-300 bg-sky-50 px-3 py-2">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-sky-800">
            {prep.title || "Prep notes"}
          </p>
          <p className="mt-0.5 text-sm font-semibold leading-snug text-sky-900">{prep.text}</p>
        </div>
      ) : null}
    </div>
  );
}

export default function ProductionTaskPage({
  role,
  design,
  hubToken = "",
  hubOrderId = "",
  isHubMode = false,
  onBack,
  onStageComplete,
}) {
  const navigate = useNavigate();
  const [accessToken, setAccessToken] = useState(isHubMode ? hubToken : "");
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(() => readLinkErrorFromQuery());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [checks, setChecks] = useState({ assembled: false, messaged: false, cutlery: false });
  const [weightProofUrl, setWeightProofUrl] = useState("");
  const [weightProofUploading, setWeightProofUploading] = useState(false);
  const [weightProofInputKey, setWeightProofInputKey] = useState(0);
  const [localWeightProofPreview, setLocalWeightProofPreview] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [callStartingPhase, setCallStartingPhase] = useState("");
  const [callCompletingPhase, setCallCompletingPhase] = useState("");
  const [frosterPhotoUrl, setFrosterPhotoUrl] = useState("");
  const [frosterPhotoUploading, setFrosterPhotoUploading] = useState(false);
  const [frosterPhotoInputKey, setFrosterPhotoInputKey] = useState(0);
  const [localFrostedPreview, setLocalFrostedPreview] = useState(null);
  const [sendingDesign, setSendingDesign] = useState(false);
  const [sendingProof, setSendingProof] = useState(false);
  const [startBusy, setStartBusy] = useState(false);
  const [crumbCoatBusy, setCrumbCoatBusy] = useState(false);

  const beforeCallConfirmed = Boolean(info?.customer_call_before?.is_confirmed);
  const afterCallConfirmed = Boolean(info?.customer_call_after?.is_confirmed);
  const designSent = Boolean(info?.decorator_customer_design_sent);
  const proofSent = Boolean(info?.decorator_customer_proof_sent);

  const resolveAccessToken = async () => {
    if (isHubMode && hubToken) return hubToken;
    return accessToken || (await resolveProductionToken(role));
  };

  const load = async () => {
    const token = await resolveAccessToken();
    if (!token) {
      throw new Error("Link is missing token. Open the full link from WhatsApp or ask manager for a fresh one.");
    }
    const res = await fetch(
      `/api/production/task?${productionTaskQuery(token, hubOrderId, role, isHubMode)}`
    );
    const data = await parseJson(res);
    setAccessToken(token);
    setInfo((prev) => mergeProductionTaskInfo(prev, data, role));
    setDone(Boolean(data.is_done));
    if (role === "decorator") {
      setChecks({
        assembled: Boolean(data?.decorator_checks?.assembled),
        messaged: Boolean(data?.decorator_checks?.messaged),
        cutlery: Boolean(data?.decorator_checks?.cutlery),
      });
      setWeightProofUrl((data?.decorator_weight_proof_url || "").trim());
    }
    if (role === "froster") {
      setFrosterPhotoUrl((data?.froster_frosted_cake_url || "").trim());
    }
  };

  useEffect(() => {
    load().catch((e) => setErr(e.message || String(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (info?.is_done) setDone(true);
  }, [info?.is_done]);

  useEffect(() => {
    if (done || !info) return undefined;
    const waiting =
      (role === "froster" && !isRtgDecorSkip(info) && !info.production_baker_done_at) ||
      (role === "decorator" &&
        !isRtgDecorSkip(info) &&
        !info.is_dry_cake &&
        !info.dry_cake_skip_froster &&
        !info.production_froster_done_at);
    if (!waiting) return undefined;
    const poll = () => load().catch(() => {});
    const interval = setInterval(poll, 5000);
    const onVis = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [role, done, info?.production_baker_done_at, info?.production_froster_done_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveChecks = async (nextChecks) => {
    const token = await resolveAccessToken();
    const res = await fetch("/api/production/task/decorator-checks", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(productionAuthBody(token, hubOrderId, role, isHubMode, nextChecks)),
    });
    const data = await parseJson(res);
    setInfo(data);
  };

  const onWeightProofCaptured = async (e) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = "";
    const remount = () => setWeightProofInputKey((k) => k + 1);
    if (!file) {
      remount();
      return;
    }
    if (role === "decorator" && !beforeCallConfirmed) {
      setErr("Complete the first customer call first, then take photos.");
      remount();
      return;
    }
    setErr("");
    setWeightProofUploading(true);
    let previewUrl = null;
    try {
      const { blob, extension } = await compressImageForUpload(file, {
        maxEdge: 1200,
        webpQuality: 0.72,
        jpegQuality: 0.72,
      });
      if (!(blob instanceof Blob) || blob.size <= 0) {
        throw new Error("Could not prepare photo — try Camera instead of Gallery.");
      }
      previewUrl = URL.createObjectURL(blob);
      setLocalWeightProofPreview(previewUrl);
      const token = await resolveAccessToken();
      const fd = productionFormAuth(token, hubOrderId, role, isHubMode, new FormData());
      fd.append("file", blob, `weight-proof.${extension}`);
      const res = await uploadFormWithRetry("/api/production/task/upload-weight-proof", fd, {
        retries: 3,
        timeoutMs: 60000,
      });
      const data = await parseJson(res);
      const uploadedUrl = (data.order?.decorator_weight_proof_url || data.url || "").trim();
      if (!uploadedUrl) {
        throw new Error("Photo saved but server URL missing — try again.");
      }
      if (data.order) {
        setInfo((prev) => (prev ? mergeProductionTaskInfo(prev, data.order, role) : data.order));
      } else {
        setInfo((prev) => (prev ? { ...prev, decorator_weight_proof_url: uploadedUrl } : prev));
      }
      setWeightProofUrl(uploadedUrl);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setLocalWeightProofPreview(null);
      setErr("");
    } catch (ex) {
      setErr(ex.message || String(ex));
    } finally {
      setWeightProofUploading(false);
      remount();
    }
  };

  const onToggleCheck = async (key, value) => {
    if (role === "decorator" && !beforeCallConfirmed) {
      setErr("Complete the first customer call first, then use the checklist.");
      return;
    }
    const next = { ...checks, [key]: value };
    setChecks(next);
    try {
      await saveChecks(next);
      setErr("");
    } catch (e) {
      setErr(e.message || String(e));
    }
  };

  const dialCustomer = (tel) => {
    const href = (tel || "").trim();
    if (!href) return;
    window.location.href = href.startsWith("tel:") ? href : `tel:${href}`;
  };

  const openCustomerWhatsApp = (phone) => {
    const href = whatsappHref(phone);
    if (!href || href === "#") return;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const onCustomerCallStart = async (phase, channel = "phone") => {
    try {
      setCallStartingPhase(phase);
      const token = await resolveAccessToken();
      const res = await fetch("/api/production/task/customer-call-start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(productionAuthBody(token, hubOrderId, role, isHubMode, { phase })),
      });
      const data = await parseJson(res);
      setInfo(data);
      setErr("");
      if (channel === "whatsapp") {
        openCustomerWhatsApp(data?.customer_phone);
      } else {
        dialCustomer(data?.customer_phone_tel);
      }
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setCallStartingPhase("");
    }
  };

  const onCustomerCallComplete = async (phase) => {
    try {
      setCallCompletingPhase(phase);
      const token = await resolveAccessToken();
      const res = await fetch("/api/production/task/customer-call-complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(productionAuthBody(token, hubOrderId, role, isHubMode, { phase })),
      });
      const data = await parseJson(res);
      setInfo(data);
      setErr("");
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setCallCompletingPhase("");
    }
  };

  const uploadFrosterPhotoFromFile = async (file) => {
    if (!file) {
      setFrosterPhotoInputKey((k) => k + 1);
      return;
    }
    setErr("");
    setFrosterPhotoUploading(true);
    let previewUrl = null;
    try {
      const { blob, extension } = await compressImageForUpload(file, {
        maxEdge: 1200,
        webpQuality: 0.72,
        jpegQuality: 0.72,
      });
      previewUrl = URL.createObjectURL(blob);
      setLocalFrostedPreview(previewUrl);
      const token = await resolveAccessToken();
      const fd = productionFormAuth(token, hubOrderId, role, isHubMode, new FormData());
      fd.append("file", blob, `frosted.${extension}`);
      const res = await fetch("/api/production/task/upload-froster-photo", {
        method: "POST",
        body: fd,
      });
      const data = await parseJson(res);
      const uploadedUrl = (data.order?.froster_frosted_cake_url || data.url || "").trim();
      if (!uploadedUrl) {
        throw new Error("Photo uploaded but server URL missing — try again");
      }
      setFrosterPhotoUrl(uploadedUrl);
      if (data.order) {
        setInfo({ ...data.order, froster_frosted_cake_url: uploadedUrl });
      } else {
        setInfo((prev) => (prev ? { ...prev, froster_frosted_cake_url: uploadedUrl } : prev));
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setLocalFrostedPreview(null);
      setErr("");
    } catch (ex) {
      setErr(ex.message || String(ex));
    } finally {
      setFrosterPhotoUploading(false);
      setFrosterPhotoInputKey((k) => k + 1);
    }
  };

  const onStageStart = async () => {
    try {
      setStartBusy(true);
      const token = await resolveAccessToken();
      const order = await postProductionStageStart({
        token,
        hubOrderId,
        role,
        isHubMode,
        productionAuthBody,
        parseJson,
      });
      setInfo((prev) => mergeProductionTaskInfo(prev, order, role));
      setErr("");
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setStartBusy(false);
    }
  };

  const onCrumbCoatDone = async () => {
    if (isRtgDecorSkip(info)) {
      setErr("RTG fridge stock — open Decorator tab for this order.");
      return;
    }
    if (!isProductionStageStarted(info, role)) {
      setErr("Tap Start at the top when you begin this job.");
      return;
    }
    if (!info?.production_baker_done_at) {
      setErr("Baker must tap Done first. This page will update automatically.");
      return;
    }
    try {
      setCrumbCoatBusy(true);
      const token = await resolveAccessToken();
      const res = await fetch("/api/production/task/crumb-coat-done", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(productionAuthBody(token, hubOrderId, role, isHubMode)),
      });
      const data = await parseJson(res);
      if (data.order) setInfo((prev) => mergeProductionTaskInfo(prev, data.order, role));
      setErr("");
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setCrumbCoatBusy(false);
    }
  };

  const onComplete = async () => {
    if (!isProductionStageStarted(info, role)) {
      setErr("Tap Start at the top when you begin this job.");
      return;
    }
    if (role === "froster") {
      if (isRtgDecorSkip(info)) {
        setErr("RTG fridge stock — open Decorator tab for this order.");
        return;
      }
      if (!(info?.production_froster_crumb_coat_done_at || "").trim()) {
        setErr("Tap Crumb Coat Done first.");
        return;
      }
      if (frosterPhotoUploading) {
        setErr("Wait for the frosted cake photo to finish uploading.");
        return;
      }
      const frostedOnServer = Boolean(
        (frosterPhotoUrl || info?.froster_frosted_cake_url || "").trim()
      );
      if (!frostedOnServer) {
        setErr("Take a frosted cake photo first. Wait for ✓ Frosted photo saved.");
        return;
      }
      if (!info?.production_baker_done_at) {
        setErr("Baker must tap Done first. This page will update automatically.");
        return;
      }
    }
    try {
      setBusy(true);
      const token = await resolveAccessToken();
      const res = await fetch("/api/production/task/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(productionAuthBody(token, hubOrderId, role, isHubMode)),
      });
      const data = await parseJson(res);
      if (data.order) setInfo(data.order);
      setDone(true);
      await load();
      setErr("");
      if (isHubMode && onStageComplete) onStageComplete();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const openLightbox = () => {
    setZoom(1);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setZoom(1);
  };

  const zoomIn = () => setZoom((z) => Math.min(4, Number((z + 0.25).toFixed(2))));
  const zoomOut = () => setZoom((z) => Math.max(1, Number((z - 0.25).toFixed(2))));

  const freshnessStamp = info ? buildFreshnessStampData(info) : null;

  const onPrintFreshnessStamp = () => {
    const orderId = info?.order_id;
    if (!orderId) return;
    navigate(`/orders/${orderId}/stamp`);
  };

  const hasWeightProof = Boolean((weightProofUrl || info?.decorator_weight_proof_url || "").trim());
  const hasFrostedPhoto = Boolean(
    (frosterPhotoUrl || info?.froster_frosted_cake_url || "").trim()
  );
  const hasCrumbCoatDone = Boolean((info?.production_froster_crumb_coat_done_at || "").trim());
  const frosterPreviewSrc = localFrostedPreview
    ? localFrostedPreview
    : hasFrostedPhoto
      ? cakeImageSrc((frosterPhotoUrl || info?.froster_frosted_cake_url || "").trim())
      : "";
  const canDecoratorComplete =
    beforeCallConfirmed &&
    afterCallConfirmed &&
    designSent &&
    proofSent &&
    checks.assembled &&
    checks.messaged &&
    checks.cutlery &&
    hasWeightProof;
  const canFrosterComplete =
    !isRtgDecorSkip(info) && hasFrostedPhoto && Boolean(info?.production_baker_done_at);
  const stageStarted = isProductionStageStarted(info, role);
  const stageStartBlocked = stageStartGateMessage(info, role);
  const canStageComplete =
    stageStarted &&
    (role === "baker" ? true : role === "froster" ? canFrosterComplete : canDecoratorComplete);
  const isBakerDeliveryDesign = role === "baker" && design === "delivery";
  const isFrosterDeliveryDesign = role === "froster" && design === "delivery";

  if (!info && !err && !isFrosterDeliveryDesign && !isBakerDeliveryDesign) {
    return (
      <div className="min-h-screen bg-[#F5F2ED] flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-[#EE2677]" />
      </div>
    );
  }

  if (isFrosterDeliveryDesign) {
    const helpTel = info?.help_phone_tel ? `tel:${String(info.help_phone_tel).trim()}` : null;
    const slotLabel = [info?.delivery_date, info?.delivery_slot || info?.custom_time]
      .filter(Boolean)
      .join(" · ");
    const tierCount = Number(info?.tiers) || 0;

    if (info && isRtgDecorSkip(info)) {
      return (
        <div className="driver-page">
          <style>{PRODUCTION_DRIVER_PAGE_CSS}</style>
          <div className="driver-top">
            {onBack ? (
              <button type="button" onClick={onBack} className="driver-back-btn">
                <ArrowLeft size={18} aria-hidden />
                All orders
              </button>
            ) : null}
          </div>
          <div className="driver-card" style={{ marginTop: 16 }}>
            <p style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>#{info.order_number} · RTG fridge cake</p>
            <p style={{ color: "#9A3412", lineHeight: 1.45 }}>
              Ye order Baking ya Frosting me nahi aata — fridge se tayyar base Decorator tab me kholo.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="driver-page">
        <style>{`${PRODUCTION_DRIVER_PAGE_CSS}${KITCHEN_TASK_SCHEDULE_BAR_CSS}`}</style>

        <div className="driver-top">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
                border: "none",
                background: "white",
                borderRadius: 999,
                padding: "10px 14px",
                fontSize: 14,
                fontWeight: 800,
                color: "#EE2677",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                cursor: "pointer",
              }}
            >
              <ArrowLeft size={18} aria-hidden />
              All orders
            </button>
          ) : null}

          <div className="driver-brand">
            <div className="driver-logo">
              <Cake size={22} />
            </div>
            <div>
              <div className="driver-title">Froster</div>
              <div className="driver-subtitle">The Table Top</div>
            </div>
          </div>
          {helpTel ? (
            <a href={helpTel} className="driver-help-btn" aria-label="Call help">
              <Headphones size={18} aria-hidden />
              Help
            </a>
          ) : null}
        </div>

        {!info && !err ? (
          <div className="driver-card" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, minHeight: 88 }}>
            <Loader2 className="h-6 w-6 animate-spin text-[#EE2677]" aria-hidden />
            <span style={{ fontWeight: 700, color: "#666" }}>Loading order…</span>
          </div>
        ) : null}

        {err ? <div className="driver-error">{err}</div> : null}

        {info ? <KitchenTaskScheduleBar info={info} role={role} done={done} /> : null}

        {info ? (
          <ProductionStageStartBar
            info={info}
            role={role}
            done={done}
            busy={startBusy}
            onStart={onStageStart}
            gateMessage={stageStartBlocked}
          />
        ) : null}

        {info?.stage_gate_message && !done ? (
          <div className="driver-success" style={{ background: "#FFF7ED", borderColor: "#FDBA74", color: "#9A3412" }}>
            {info.stage_gate_message}
          </div>
        ) : null}

        {done ? (
          <div className="driver-success">
            <CheckCircle2 size={18} />
            Froster stage complete
          </div>
        ) : null}

        {info ? (
          <>
            <PriorityOrderBanner order={info} active={taskPriorityActive(info)} />
            <div className={`driver-card ${taskPriorityActive(info) ? "driver-priority-card" : ""}`}>
              <div className="driver-label">Order</div>
              <div className={`driver-order-num ${taskPriorityActive(info) ? "!text-green-700" : ""}`}>#{info.order_number}</div>
              <div className="driver-customer-name">{displayCustomerName(info.customer_name || "Customer")}</div>
              {slotLabel ? <div className="driver-slot">{slotLabel}</div> : null}
            </div>

            {!info.is_cheesecake ? <OrderBakingSpecDisplay info={info} variant="driver" /> : null}

            {(info.flavor || info.weight_kg || isPastryTask(info)) ? (
              <div className="driver-card" style={{ padding: "14px 16px" }}>
                <div className="driver-label">{isPastryTask(info) ? "Pastry" : "Frost"}</div>
                <div className="driver-frost-line">
                  {info.flavor ? (
                    <span className="driver-frost-chip driver-frost-chip--flavor">
                      {info.flavor.replace(/ Cake$/, "")}
                    </span>
                  ) : null}
                  {taskWeightOrPiecesLabel(info) ? (
                    <span className="driver-frost-chip driver-frost-chip--weight">
                      {taskWeightOrPiecesLabel(info)}
                    </span>
                  ) : null}
                  {info.order_type ? (
                    <span
                      className={`driver-frost-tag ${
                        info.order_type === "RTG"
                          ? "driver-frost-tag--rtg"
                          : info.order_type === "Pastry"
                          ? "driver-frost-tag--custom"
                          : "driver-frost-tag--custom"
                      }`}
                    >
                      {info.order_type}
                    </span>
                  ) : null}
                  {tierCount > 0 ? (
                    <span className="driver-frost-tag driver-frost-tag--tier">
                      {tierCount} {tierCount === 1 ? "tier" : "tiers"}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}

            {taskReferenceImage(info) ? (
              <div className="driver-card" style={{ padding: 12 }}>
                <button
                  type="button"
                  onClick={openLightbox}
                  style={{ display: "block", width: "100%", border: "none", background: "none", padding: 0 }}
                >
                  <img
                    src={cakeImageSrc(taskReferenceImage(info))}
                    alt={isPastryTask(info) ? "Pastry reference" : "Cake reference"}
                    className="driver-cake-img"
                  />
                </button>
              </div>
            ) : null}

            {isPastryTask(info) && info.pastry_box_instruction ? (
              <div className="driver-notes-card">
                <div className="driver-label" style={{ color: "#BE185D", marginBottom: 6 }}>
                  Pastry
                </div>
                {info.pastry_box_instruction}
              </div>
            ) : null}

            {info.baker_notes ? (
              <div className="driver-notes-card">
                <div className="driver-label" style={{ color: "#92400E", marginBottom: 6 }}>
                  Baker notes
                </div>
                {info.baker_notes}
              </div>
            ) : null}

            {(info.message_on_cake || info.order_notes) ? (
              <div className="driver-card">
                {info.message_on_cake ? (
                  <div className="driver-detail-line">
                    <strong>Message on cake:</strong> {info.message_on_cake}
                  </div>
                ) : null}
                {info.order_notes ? (
                  <div className="driver-detail-line">
                    <strong>Order notes:</strong> {info.order_notes}
                  </div>
                ) : null}
              </div>
            ) : null}

            <FrosterStructureTable
              blocks={info.frosting_structures}
              orderType={info.order_type}
              tiers={info.tiers}
              weightKg={info.weight_kg}
              design="driver"
            />

            <FrosterPhotoCapture
              variant="driver"
              previewSrc={frosterPreviewSrc}
              hasSaved={hasFrostedPhoto}
              uploading={frosterPhotoUploading}
              disabled={busy || done || !info.production_baker_done_at}
              inputKey={frosterPhotoInputKey}
              onFileSelected={uploadFrosterPhotoFromFile}
            />
          </>
        ) : null}

        {info ? (
          <div className="driver-footer">
            {!hasCrumbCoatDone && !done ? (
              <button
                type="button"
                disabled={crumbCoatBusy || !stageStarted || !info.production_baker_done_at}
                onClick={onCrumbCoatDone}
                className="driver-btn-done"
              >
                {crumbCoatBusy ? "Saving…" : !stageStarted ? "Tap Start first" : "Crumb Coat Done"}
              </button>
            ) : (
              <button
                type="button"
                disabled={busy || done || !canStageComplete || frosterPhotoUploading || !stageStarted}
                onClick={onComplete}
                className={`driver-btn-done${done ? " driver-btn-done--complete" : ""}`}
              >
                {busy ? "Saving…" : !stageStarted ? "Tap Start first" : frosterPhotoUploading ? "Uploading photo…" : done ? "✓ Froster done" : "Froster done"}
              </button>
            )}
          </div>
        ) : null}

        {lightboxOpen && info?.cake_image_url ? (
          <div className="fixed inset-0 z-50 bg-black/90 px-3 py-4">
            <div className="mx-auto flex h-full w-full max-w-4xl flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">Cake reference</p>
                <button
                  type="button"
                  onClick={closeLightbox}
                  className="rounded-full bg-white/20 px-3 py-1 text-sm font-bold text-white"
                >
                  Close
                </button>
              </div>
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={zoomOut}
                  disabled={zoom <= 1}
                  className="rounded-full bg-white/20 px-3 py-1 text-sm font-bold text-white disabled:opacity-40"
                >
                  −
                </button>
                <p className="min-w-[64px] text-center text-sm font-semibold text-white">
                  {Math.round(zoom * 100)}%
                </p>
                <button
                  type="button"
                  onClick={zoomIn}
                  disabled={zoom >= 4}
                  className="rounded-full bg-white/20 px-3 py-1 text-sm font-bold text-white disabled:opacity-40"
                >
                  +
                </button>
              </div>
              <div className="flex-1 overflow-auto rounded-2xl border border-white/20 bg-black/40 p-2">
                <div className="flex min-h-full min-w-full items-center justify-center">
                  <img
                    src={info.cake_image_url}
                    alt="Cake reference full size"
                    className="max-h-none max-w-none rounded-xl object-contain"
                    style={{ transform: `scale(${zoom})`, transformOrigin: "center center" }}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (isBakerDeliveryDesign) {
    const helpTel = info?.help_phone_tel ? `tel:${String(info.help_phone_tel).trim()}` : null;
    return (
      <div className="min-h-screen bg-[#F5F2EA] px-3 pb-24 pt-3">
        <style>{KITCHEN_TASK_SCHEDULE_BAR_CSS}</style>
        <div className="mx-auto max-w-md">
          <div className="sticky top-0 z-20 mb-3 flex items-start justify-between gap-2 bg-[#F5F2EA] pb-2 pt-1">
            <div className="flex flex-col gap-2">
              {onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    border: "none",
                    background: "white",
                    borderRadius: 999,
                    padding: "8px 12px",
                    fontSize: 13,
                    fontWeight: 800,
                    color: "#EE2677",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    cursor: "pointer",
                    width: "fit-content",
                  }}
                >
                  <ArrowLeft size={16} aria-hidden />
                  All orders
                </button>
              ) : null}
            <div className="flex items-center gap-2">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#EE2677] text-white">
                <Truck size={20} />
              </div>
              <div>
                <p className="text-[18px] font-extrabold leading-tight text-[#2D2D2D]">Baker</p>
                <p className="text-xs text-[#737373]">The Table Top</p>
              </div>
            </div>
            </div>
            {helpTel ? (
              <a
                href={helpTel}
                className="inline-flex items-center gap-1 rounded-full bg-[#DC2626] px-3 py-2 text-xs font-extrabold text-white shadow"
              >
                <Headphones size={16} />
                Help
              </a>
            ) : null}
          </div>

          <PriorityOrderBanner order={info} className="mb-3" active={taskPriorityActive(info)} />
          {info ? <KitchenTaskScheduleBar info={info} role={role} done={done} /> : null}
          {info ? (
            <ProductionStageStartBar
              info={info}
              role={role}
              done={done}
              busy={startBusy}
              onStart={onStageStart}
              gateMessage={stageStartBlocked}
            />
          ) : null}
          <div className={`mb-3 rounded-[18px] border p-4 shadow-sm ${taskPriorityActive(info) ? "production-priority-card" : "border-[#EBB3A9] bg-white"}`}>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A8A]">Order</div>
            <div className={`text-lg font-bold ${taskPriorityActive(info) ? "text-green-700" : "text-[#2D2D2D]"}`}>#{info?.order_number || "—"}</div>
            <div className={`mt-1 text-[40px] font-black leading-none customer-name-text ${taskPriorityActive(info) ? "text-green-700" : "text-[#EE2677]"}`}>{displayCustomerName(info?.customer_name || "Customer")}</div>
            <div className="mt-2 text-sm text-[#666]">
              {info?.delivery_date || "-"} · {info?.delivery_slot || info?.custom_time || "-"}
            </div>
          </div>

          <div className="mb-3 rounded-[18px] border border-[#EBB3A9] bg-white p-3 shadow-sm">
            {info?.cake_image_url ? (
              <button type="button" onClick={openLightbox} className="block w-full overflow-hidden rounded-xl border border-[#F2D4CE] bg-[#FFF8F7] p-2">
                <img src={cakeImageSrc(info.cake_image_url)} alt="Cake reference" className="h-56 w-full rounded-lg object-cover" />
              </button>
            ) : (
              <div className="flex h-56 items-center justify-center rounded-xl border border-dashed border-[#E6B8AE] bg-[#FFF8F7] text-sm text-[#777]">
                Cake photo not available
              </div>
            )}
            <div className="mt-3 space-y-2 text-base leading-tight text-[#20242C]">
              {info?.is_cheesecake ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-[#EE2677] px-3 py-1 text-sm font-extrabold text-white">
                      Cheese Cake
                    </span>
                    {info?.flavor ? (
                      <span className="rounded-full bg-[#FCE7F3] px-3 py-1 text-sm font-bold text-[#C2185B]">
                        {info.flavor}
                      </span>
                    ) : null}
                    {info?.weight_kg ? (
                      <span className="rounded-full bg-[#FCE7F3] px-3 py-1 text-sm font-bold text-[#C2185B]">
                        {info.weight_kg} kg
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm text-[#555]">
                    Fixed cheesecake recipe — no flour or sweetener swap. Water-bath bake; top filling goes to froster.
                  </p>
                </div>
              ) : (
                <OrderBakingSpecDisplay info={info} />
              )}
              <p><span className="font-extrabold">Order notes:</span> {info?.order_notes || "-"}</p>
            </div>

            <BakerPrepIntel info={info} />

            {info?.ingredient_breakdown?.blocks?.length > 0 ? (
              <div className="mt-4 rounded-2xl border-2 border-[#EE2677]/25 bg-gradient-to-b from-white to-[#FFF7FA] p-3 shadow-sm">
                <p className="text-sm font-extrabold uppercase tracking-wide text-[#2D2D2D]">
                  {info?.is_cheesecake ? "Cheesecake recipe (bake only)" : "Ingredient breakdown"}
                </p>
                <p className="mt-0.5 text-xs text-[#666]">
                  {info?.is_cheesecake
                    ? "Crust + filling scaled for this size — top filling is on the froster sheet."
                    : "Scaled for this order — weigh dry and wet separately."}
                </p>

                <div className="mt-3 space-y-3">
                  {info.ingredient_breakdown.blocks.map((block, index) => (
                    <div
                      key={`${block.label}-${index}`}
                      className="overflow-hidden rounded-2xl border border-[#EBB3A9]/50 bg-white"
                    >
                      <div className="border-b border-[#EBB3A9]/40 bg-[#EE2677]/10 px-3 py-2">
                        <CakeBlockLabel label={block.label} />
                        {!info?.is_cheesecake && !block.is_cheesecake ? (
                          <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] font-semibold">
                            {formatKitchenTierLabel(info?.tiers) ? (
                              <span className="rounded-full border-2 border-violet-500 bg-violet-100 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wide text-violet-900">
                                {formatKitchenTierLabel(info.tiers)}
                              </span>
                            ) : null}
                            <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-900">
                              Base: {block.base_flour || info?.base_flour || "Maida"}
                            </span>
                            <span className="rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-sky-900">
                              Sweetener: {block.sweetener || info?.sweetener || "Sugar"}
                            </span>
                            {(block.base_fat || info?.base_fat) === "Butter" ? (
                              <span className="rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-rose-900">
                                Butter
                                {(block.fat_upsell || 0) > 0
                                  ? ` (+₹${Math.round(block.fat_upsell)})`
                                  : ""}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>

                      <div className="flex flex-col gap-2 p-2">
                        <IngredientList
                          title="Dry"
                          subtitle={`${block.dry_grams ?? 0} g`}
                          items={block.dry_items}
                          accent={{
                            border: "border-amber-200",
                            bg: "bg-amber-50/80",
                            title: "text-amber-900",
                            subtitle: "text-amber-700",
                            divider: "border-amber-200/70",
                          }}
                        />
                        <IngredientList
                          title="Wet"
                          subtitle={`${block.wet_grams ?? 0} g`}
                          items={block.wet_items}
                          accent={{
                            border: "border-sky-200",
                            bg: "bg-sky-50/80",
                            title: "text-sky-900",
                            subtitle: "text-sky-700",
                            divider: "border-sky-200/70",
                          }}
                        />
                      </div>

                      <div className="flex flex-wrap gap-2 border-t border-[#EBB3A9]/30 bg-[#FAFAFA] px-3 py-2 text-[11px] font-semibold text-[#555]">
                        <span>Dry: {block.dry_grams ?? 0} g</span>
                        <span>·</span>
                        <span>Wet: {block.wet_grams ?? 0} g</span>
                        <span>·</span>
                        <span className="text-[#C2185B]">Total: {block.total_grams ?? 0} g</span>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="mt-3 rounded-xl bg-[#EE2677]/10 px-3 py-2 text-center text-xs font-bold text-[#C2185B]">
                  Grand total input: {info?.ingredient_breakdown?.grand_total_grams || 0} g
                </p>
              </div>
            ) : null}
          </div>

          {err ? <div className="mb-3 rounded-xl bg-red-50 p-3 text-sm text-red-700">{err}</div> : null}

          {isHubMode && hubOrderId && accessToken ? (
            <div className="mb-3">
              <KitchenUnitLabel
                token={accessToken}
                orderId={hubOrderId}
                role="baker"
                orderInfo={info}
              />
            </div>
          ) : null}
        </div>

        <div className="fixed bottom-0 left-0 right-0 mx-auto max-w-md bg-gradient-to-t from-[#F5F2EA] to-transparent px-3 pb-3 pt-4">
          {done ? (
            <div className="rounded-full border border-green-300 bg-green-50 py-3 text-center text-lg font-bold text-green-700">
              Done
            </div>
          ) : (
            <Button
              type="button"
              disabled={busy || !stageStarted}
              onClick={onComplete}
              className="h-14 w-full rounded-full bg-[#EE2677] text-lg font-extrabold text-white hover:bg-[#d91f69]"
            >
              {busy ? "Saving..." : !stageStarted ? "Tap Start first" : "Baker Done"}
            </Button>
          )}
        </div>

        {lightboxOpen && info?.cake_image_url ? (
          <div className="fixed inset-0 z-50 bg-black/90 px-3 py-4">
            <div className="mx-auto flex h-full w-full max-w-4xl flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">Cake Reference</p>
                <button
                  type="button"
                  onClick={closeLightbox}
                  className="rounded-full bg-white/20 px-3 py-1 text-sm font-bold text-white"
                >
                  Close
                </button>
              </div>
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={zoomOut}
                  disabled={zoom <= 1}
                  className="rounded-full bg-white/20 px-3 py-1 text-sm font-bold text-white disabled:opacity-40"
                >
                  -
                </button>
                <p className="min-w-[64px] text-center text-sm font-semibold text-white">{Math.round(zoom * 100)}%</p>
                <button
                  type="button"
                  onClick={zoomIn}
                  disabled={zoom >= 4}
                  className="rounded-full bg-white/20 px-3 py-1 text-sm font-bold text-white disabled:opacity-40"
                >
                  +
                </button>
              </div>
              <div className="flex-1 overflow-auto rounded-2xl border border-white/20 bg-black/40 p-2">
                <div className="flex min-h-full min-w-full items-center justify-center">
                  <img
                    src={info.cake_image_url}
                    alt="Cake reference full size"
                    className="max-h-none max-w-none rounded-xl object-contain"
                    style={{ transform: `scale(${zoom})`, transformOrigin: "center center" }}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F2ED] px-3 py-4">
      <style>{KITCHEN_TASK_SCHEDULE_BAR_CSS}</style>
      <div className="mx-auto max-w-md space-y-3">
        <div className="flex items-center justify-between rounded-3xl bg-white px-3 py-2 shadow-sm">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#9A9A9A]">{roleTitle[role] || "Production"}</p>
            <p className="text-sm font-semibold text-[#30343B]">The Table Top</p>
          </div>
        </div>

        <PriorityOrderBanner order={info} className="mb-1" active={taskPriorityActive(info)} />

        {info ? <KitchenTaskScheduleBar info={info} role={role} done={done} /> : null}

        {info ? (
          <ProductionStageStartBar
            info={info}
            role={role}
            done={done}
            busy={startBusy}
            onStart={onStageStart}
            gateMessage={stageStartBlocked}
          />
        ) : null}

        <div className={`rounded-3xl border p-4 ${taskPriorityActive(info) ? "production-priority-card" : "border-[#EBB3A9] bg-[#F1F1F1]"}`}>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A]">Order</p>
          <h1 className={`mt-1 text-[44px] font-extrabold leading-none ${taskPriorityActive(info) ? "text-green-700" : "text-[#EE2677]"}`}>#{info?.order_number || "—"}</h1>
          <p className="mt-1 text-[22px] font-bold leading-tight text-[#2D2D2D] break-words customer-name-text">
            {displayCustomerName(info?.customer_name || "Customer")}
          </p>
          <p className="mt-1 text-[16px] leading-tight text-[#555]">
            {info?.delivery_date || "-"} · {info?.delivery_slot || info?.custom_time || "-"}
          </p>
        </div>

        <div className="rounded-3xl border border-[#EBB3A9] bg-[#F1F1F1] p-3">
          {taskReferenceImage(info) ? (
            <div className="rounded-2xl border border-[#EFC8C2] bg-[#F8F8F8] p-2">
              <button
                type="button"
                onClick={openLightbox}
                className="block w-full overflow-hidden rounded-xl"
              >
                <img
                  src={cakeImageSrc(taskReferenceImage(info))}
                  alt={isPastryTask(info) ? "Pastry reference" : "Cake reference"}
                  className="h-56 w-full rounded-xl object-cover transition-transform hover:scale-[1.01]"
                />
              </button>
            </div>
          ) : (
            <div className="h-56 w-full rounded-2xl border border-dashed border-[#EBB3A9] bg-[#F8F8F8] flex items-center justify-center text-sm text-[#777]">
              {isPastryTask(info) ? "Pastry photo not available" : "Cake photo not available"}
            </div>
          )}

          {isPastryTask(info) && info?.pastry_box_instruction ? (
            <div className="mt-3 rounded-2xl border border-[#F9A8D4] bg-[#FCE7F3] px-3 py-2 text-sm font-bold text-[#BE185D]">
              {info.pastry_box_instruction}
            </div>
          ) : null}

          <div className="mt-3 space-y-1 text-[20px] leading-tight text-[#20242C]">
            {role !== "baker" ? (
              <>
                <p><span className="font-extrabold">Flavor:</span> {info?.flavor || "-"}</p>
                <p>
                  <span className="font-extrabold">{isPastryTask(info) ? "Pieces:" : "Weight:"}</span>{" "}
                  {isPastryTask(info)
                    ? taskWeightOrPiecesLabel(info) || "-"
                    : `${info?.weight_kg || "-"} kg`}
                </p>
                <p><span className="font-extrabold">Qty:</span> {info?.quantity || 1}</p>
              </>
            ) : null}
            {role === "froster" && info?.order_type ? (
              <p>
                <span className="font-extrabold">Order type:</span>{" "}
                <span className="rounded-full bg-sky-100 px-2 py-0.5 text-base font-bold text-sky-900">
                  {info.order_type}
                </span>
              </p>
            ) : null}
            {role === "baker" && info?.is_cheesecake ? (
              <div className="space-y-1">
                <p><span className="font-extrabold">Flavor:</span> {info?.flavor || "-"}</p>
                <p><span className="font-extrabold">Size:</span> {info?.weight_kg || "-"} kg</p>
                <p className="text-sm text-[#555]">Cheese cake — use recipe from Baking tab (no flour/sweetener swap).</p>
              </div>
            ) : null}
            {(role === "baker" || role === "froster") && !info?.is_cheesecake ? (
              <OrderBakingSpecDisplay info={info} />
            ) : null}
            {role !== "baker" ? (
              <>
                <p><span className="font-extrabold">Message:</span> {info?.message_on_cake || "-"}</p>
                <p><span className="font-extrabold">Baker notes:</span> {info?.baker_notes || "-"}</p>
              </>
            ) : null}
            <p><span className="font-extrabold">Order notes:</span> {info?.order_notes || "-"}</p>
          </div>

            {role === "froster" ? (
              <FrosterStructureTable
                blocks={info?.frosting_structures}
                orderType={info?.order_type}
                tiers={info?.tiers}
                weightKg={info?.weight_kg}
              />
            ) : null}

          {role === "baker" ? <BakerPrepIntel info={info} /> : null}

          {role === "baker" && info?.ingredient_breakdown?.blocks?.length > 0 ? (
            <div className="mt-4 rounded-2xl border-2 border-[#EE2677]/25 bg-gradient-to-b from-white to-[#FFF7FA] p-3 shadow-sm">
              <p className="text-sm font-extrabold uppercase tracking-wide text-[#2D2D2D]">
                Ingredient breakdown
              </p>
              <p className="mt-0.5 text-xs text-[#666]">
                Scaled for this order — weigh dry and wet separately.
              </p>

              <div className="mt-3 space-y-3">
                {info.ingredient_breakdown.blocks.map((block, index) => (
                  <div
                    key={`${block.label}-${index}`}
                    className="overflow-hidden rounded-2xl border border-[#EBB3A9]/50 bg-white"
                  >
                    <div className="border-b border-[#EBB3A9]/40 bg-[#EE2677]/10 px-3 py-2">
                      <CakeBlockLabel label={block.label} />
                      {!info?.is_cheesecake && !block.is_cheesecake ? (
                        <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] font-semibold">
                          {formatKitchenTierLabel(info?.tiers) ? (
                            <span className="rounded-full border-2 border-violet-500 bg-violet-100 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wide text-violet-900">
                              {formatKitchenTierLabel(info.tiers)}
                            </span>
                          ) : null}
                          <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-900">
                            Base: {block.base_flour || info?.base_flour || "Maida"}
                          </span>
                          <span className="rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-sky-900">
                            Sweetener: {block.sweetener || info?.sweetener || "Sugar"}
                          </span>
                          {(block.base_fat || info?.base_fat) === "Butter" ? (
                            <span className="rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-rose-900">
                              Butter
                              {(block.fat_upsell || 0) > 0
                                ? ` (+₹${Math.round(block.fat_upsell)})`
                                : ""}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-col gap-2 p-2">
                      <IngredientList
                        title="Dry"
                        subtitle={`${block.dry_grams ?? 0} g`}
                        items={block.dry_items}
                        accent={{
                          border: "border-amber-200",
                          bg: "bg-amber-50/80",
                          title: "text-amber-900",
                          subtitle: "text-amber-700",
                          divider: "border-amber-200/70",
                        }}
                      />
                      <IngredientList
                        title="Wet"
                        subtitle={`${block.wet_grams ?? 0} g`}
                        items={block.wet_items}
                        accent={{
                          border: "border-sky-200",
                          bg: "bg-sky-50/80",
                          title: "text-sky-900",
                          subtitle: "text-sky-700",
                          divider: "border-sky-200/70",
                        }}
                      />
                    </div>

                    <div className="flex flex-wrap gap-2 border-t border-[#EBB3A9]/30 bg-[#FAFAFA] px-3 py-2 text-[11px] font-semibold text-[#555]">
                      <span>Dry: {block.dry_grams ?? 0} g</span>
                      <span>·</span>
                      <span>Wet: {block.wet_grams ?? 0} g</span>
                      <span>·</span>
                      <span className="text-[#C2185B]">Total: {block.total_grams ?? 0} g</span>
                    </div>
                  </div>
                ))}
              </div>

              <p className="mt-3 rounded-xl bg-[#EE2677]/10 px-3 py-2 text-center text-xs font-bold text-[#C2185B]">
                Grand total input: {info?.ingredient_breakdown?.grand_total_grams || 0} g
              </p>
            </div>
          ) : null}
        </div>

        {role === "decorator" && (
          <>
            <ProductionCustomerCall
              info={info}
              callInfo={info?.customer_call_before}
              phase="before"
              busy={busy}
              done={done}
              starting={callStartingPhase === "before"}
              completing={callCompletingPhase === "before"}
              onStartCall={() => onCustomerCallStart("before", "phone")}
              onStartWhatsAppCall={() => onCustomerCallStart("before", "whatsapp")}
              onCompleteCall={() => onCustomerCallComplete("before")}
            />

            <div
              className={`rounded-3xl border border-[#EBB3A9] bg-[#F1F1F1] p-3 ${
                !beforeCallConfirmed ? "opacity-50 pointer-events-none" : ""
              }`}
            >
              <p className="mb-2 text-base font-bold text-[#2D2D2D]">Decorator checklist</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  ["assembled", "Assembled"],
                  ["messaged", "Messaged"],
                  ["cutlery", "Cutlery"],
                ].map(([key, label]) => {
                  const checked = checks[key];
                  return (
                    <label
                      key={key}
                      className={`flex items-center justify-center gap-1 rounded-xl border px-2 py-2 text-xs font-semibold cursor-pointer transition-colors ${
                        checked
                          ? "border-[#EE2677] bg-[#FFEAF2] text-[#C2185B]"
                          : "border-[#D9D9D9] bg-white text-[#555]"
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={!beforeCallConfirmed}
                        onCheckedChange={(v) => onToggleCheck(key, v === true)}
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div
              className={`rounded-3xl border border-[#EBB3A9] bg-white p-3 ${
                !beforeCallConfirmed ? "opacity-50 pointer-events-none" : ""
              }`}
            >
              <div className="mb-2 flex items-center gap-2">
                <Scale className="h-5 w-5 text-[#EE2677]" />
                <p className="text-base font-bold text-[#2D2D2D]">Weight proof photo</p>
              </div>
              <p className="mb-3 text-xs text-[#666]">
                Take a clear photo of the finished cake on the weighing scale. Customer receives this
                when you tap Done.
              </p>
              {localWeightProofPreview || weightProofUrl || info?.decorator_weight_proof_url ? (
                <img
                  src={cakeImageSrc(
                    localWeightProofPreview ||
                      weightProofUrl ||
                      info?.decorator_weight_proof_url
                  )}
                  alt="Cake on weighing scale"
                  className="mb-3 w-full rounded-2xl border border-[#EBB3A9] object-cover"
                />
              ) : null}
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-full border-2 border-dashed border-[#EE2677]/50 bg-[#FFF7FA] px-4 py-3 text-sm font-bold text-[#C2185B]">
                <Camera className="h-5 w-5" />
                {weightProofUploading
                  ? "Uploading..."
                  : hasWeightProof
                    ? "Retake scale photo"
                    : "Take scale photo"}
                <input
                  key={weightProofInputKey}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  disabled={weightProofUploading || busy}
                  onChange={onWeightProofCaptured}
                />
              </label>
            </div>

            <div className="rounded-3xl border border-[#EBB3A9] bg-white p-3">
              <div className="mb-2 flex items-center gap-2">
                <Stamp className="h-5 w-5 text-[#EE2677]" aria-hidden />
                <p className="text-base font-bold text-[#2D2D2D]">Freshness stamp</p>
              </div>
              <p className="mb-3 text-xs text-[#666]">
                Opens the physical stamp in a new tab. Blank lines until baker and froster mark Done.
                Use by is 5 days after baking (IST).
                {freshnessStamp?.weightLabel
                  ? ` Order weight: ${freshnessStamp.weightLabel}.`
                  : ""}
              </p>
              <div className="mb-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-xl border border-[#EBB3A9]/60 bg-[#FFF7FA] px-2.5 py-2">
                  <p className="font-bold uppercase tracking-wide text-[#888]">Baked on</p>
                  <p className="font-extrabold text-[#C2185B]">{freshnessStamp?.bakedOn || "—"}</p>
                </div>
                <div className="rounded-xl border border-[#EBB3A9]/60 bg-[#FFF7FA] px-2.5 py-2">
                  <p className="font-bold uppercase tracking-wide text-[#888]">Baked At</p>
                  <p className="font-extrabold text-[#C2185B]">{freshnessStamp?.bakedAt || "—"}</p>
                </div>
                <div className="rounded-xl border border-[#EBB3A9]/60 bg-[#FFF7FA] px-2.5 py-2">
                  <p className="font-bold uppercase tracking-wide text-[#888]">Use by</p>
                  <p className="font-extrabold text-[#C2185B]">{freshnessStamp?.useBy || "—"}</p>
                </div>
                <div className="rounded-xl border border-[#EBB3A9]/60 bg-[#FFF7FA] px-2.5 py-2">
                  <p className="font-bold uppercase tracking-wide text-[#888]">Frosting time</p>
                  <p className="font-extrabold text-[#C2185B]">{freshnessStamp?.frostingTime || "—"}</p>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={onPrintFreshnessStamp}
                className="h-12 w-full rounded-full border-2 border-[#EE2677] text-base font-bold text-[#C2185B] hover:bg-[#FFF0F5]"
              >
                Stamp
              </Button>
            </div>

            {proofSent ? (
              <ProductionCustomerCall
                info={info}
                callInfo={info?.customer_call_after}
                phase="after"
                busy={busy}
                done={done}
                starting={callStartingPhase === "after"}
                completing={callCompletingPhase === "after"}
                onStartCall={() => onCustomerCallStart("after", "phone")}
                onStartWhatsAppCall={() => onCustomerCallStart("after", "whatsapp")}
                onCompleteCall={() => onCustomerCallComplete("after")}
              />
            ) : null}
          </>
        )}

        {role === "froster" ? (
          <FrosterPhotoCapture
            variant="default"
            previewSrc={frosterPreviewSrc}
            hasSaved={hasFrostedPhoto}
            uploading={frosterPhotoUploading}
            disabled={busy || done}
            inputKey={frosterPhotoInputKey}
            onFileSelected={uploadFrosterPhotoFromFile}
          />
        ) : null}

        {isHubMode && hubOrderId && accessToken && (role === "baker" || role === "froster" || role === "decorator") ? (
          <div className="mb-3">
            <KitchenUnitLabel
              token={accessToken}
              orderId={hubOrderId}
              role={role}
              orderInfo={info}
            />
          </div>
        ) : null}

        {err ? <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{err}</div> : null}

        {done ? (
          <div className="rounded-3xl border border-green-300 bg-green-50 p-4 text-center">
            <CheckCircle2 className="mx-auto h-8 w-8 text-green-700" />
            <p className="mt-1 text-xl font-bold text-green-700">Done</p>
          </div>
        ) : role === "froster" && !hasCrumbCoatDone ? (
          <Button
            type="button"
            disabled={crumbCoatBusy || !stageStarted || !info?.production_baker_done_at}
            onClick={onCrumbCoatDone}
            className="h-14 w-full rounded-full bg-[#EE2677] text-xl font-extrabold text-white hover:bg-[#d91f69]"
          >
            {crumbCoatBusy ? "Saving..." : !stageStarted ? "Tap Start first" : "Crumb Coat Done"}
          </Button>
        ) : (
          <Button
            type="button"
            disabled={busy || !canStageComplete || (role === "froster" && frosterPhotoUploading) || !stageStarted}
            onClick={onComplete}
            className="h-14 w-full rounded-full bg-[#EE2677] text-xl font-extrabold text-white hover:bg-[#d91f69]"
          >
            {busy ? "Saving..." : !stageStarted ? "Tap Start first" : role === "froster" && frosterPhotoUploading ? "Uploading photo..." : `${roleTitle[role] || "Task"} Done`}
          </Button>
        )}
      </div>

      {lightboxOpen && info?.cake_image_url ? (
        <div className="fixed inset-0 z-50 bg-black/90 px-3 py-4">
          <div className="mx-auto flex h-full w-full max-w-4xl flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-white">Cake Reference</p>
              <button
                type="button"
                onClick={closeLightbox}
                className="rounded-full bg-white/20 px-3 py-1 text-sm font-bold text-white"
              >
                Close
              </button>
            </div>

            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={zoomOut}
                disabled={zoom <= 1}
                className="rounded-full bg-white/20 px-3 py-1 text-sm font-bold text-white disabled:opacity-40"
              >
                -
              </button>
              <p className="min-w-[64px] text-center text-sm font-semibold text-white">{Math.round(zoom * 100)}%</p>
              <button
                type="button"
                onClick={zoomIn}
                disabled={zoom >= 4}
                className="rounded-full bg-white/20 px-3 py-1 text-sm font-bold text-white disabled:opacity-40"
              >
                +
              </button>
            </div>

            <div className="flex-1 overflow-auto rounded-2xl border border-white/20 bg-black/40 p-2">
              <div className="flex min-h-full min-w-full items-center justify-center">
                <img
                  src={info.cake_image_url}
                  alt="Cake reference full size"
                  className="max-h-none max-w-none rounded-xl object-contain"
                  style={{ transform: `scale(${zoom})`, transformOrigin: "center center" }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
